import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { NewPreemieProvider, useNewPreemieContext } from './NewPreemieContext';
import { ChecklistsConfigProvider } from './ChecklistsConfigProvider';
import { onStorageChange } from './storage';
import OpeningConditions from './OpeningConditions';
import Medications, { useMedicationsStatus } from './sections/NewPreemieMedications';
import RoomEquipment, { useRoomEquipmentStatus } from './sections/NewPreemieRoomEquipment';
import VentilationEquipment, { useVentilationEquipmentStatus } from './sections/NewPreemieVentilationEquipment';
import IntubationEquipment, { useIntubationEquipmentStatus } from './sections/NewPreemieIntubationEquipment';
import Apgar, { useApgarStatus } from './sections/Apgar';
import UmbilicalCord, { useUmbilicalCordStatus } from './sections/NewPreemieUmbilicalCord';
import DepartmentAdmission, { useDepartmentAdmissionStatus } from './sections/NewPreemieDepartmentAdmission';
import Nutrition, { useNutritionStatus } from './sections/NewPreemieNutrition';
import Parents, { useParentsStatus } from './sections/NewPreemieParents';
import AppsHeader from '../components/AppsHeader';
import { ChevronUp, ChevronLeft } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPills,
  faBed,
  faLungs,
  faRepeat,
  faClock,
  faHospital,
  faFileLines,
  faPersonBreastfeeding,
  faUtensils,
} from '@fortawesome/free-solid-svg-icons';
import { NewPreemiePDFExportButton } from './pdf/NewPreemiePDFExportButton';
import './NewPreemie.scss';

type SectionName = 'openingConditions' | 'medications' | 'roomEquipment' | 'ventilationEquipment' | 'intubationEquipment' | 'apgar' | 'umbilicalCord' | 'departmentAdmission' | 'nutrition' | 'parents';

interface NewPreemieContentProps {
  hospital?: string;
}

const NewPreemieContent: React.FC<NewPreemieContentProps> = ({ hospital }) => {
  const { anamnesis, birthReason, weightAssessment, gestationalAgeWeeks, gestationalAgeDays, gender, resetContext } = useNewPreemieContext();
  
  // State to trigger re-renders when checkboxes change
  const [statusUpdateTrigger, setStatusUpdateTrigger] = useState(0);
  
  // Get status for each section - will re-calculate when statusUpdateTrigger changes
  const medicationsStatus = useMemo(() => useMedicationsStatus(), [statusUpdateTrigger]);
  const roomEquipmentStatus = useMemo(() => useRoomEquipmentStatus(), [statusUpdateTrigger]);
  const ventilationEquipmentStatus = useMemo(() => useVentilationEquipmentStatus(), [statusUpdateTrigger]);
  const intubationEquipmentStatus = useMemo(() => useIntubationEquipmentStatus(), [statusUpdateTrigger]);
  const apgarStatus = useMemo(() => useApgarStatus(), [statusUpdateTrigger]);
  const umbilicalCordStatus = useMemo(() => useUmbilicalCordStatus(), [statusUpdateTrigger]);
  const departmentAdmissionStatus = useMemo(() => useDepartmentAdmissionStatus(), [statusUpdateTrigger]);
  const nutritionStatus = useMemo(() => useNutritionStatus(), [statusUpdateTrigger]);
  const parentsStatus = useMemo(() => useParentsStatus(), [statusUpdateTrigger]);
  
  // Listen for storage changes to update status
  useEffect(() => {
    const unsubscribe = onStorageChange(() => {
      setStatusUpdateTrigger(prev => prev + 1);
    });
    
    return unsubscribe;
  }, []);
  
  const [expandedSections, setExpandedSections] = useState<Record<SectionName, boolean>>({
    openingConditions: false,
    medications: false,
    roomEquipment: false,
    ventilationEquipment: false,
    intubationEquipment: false,
    apgar: false,
    umbilicalCord: false,
    departmentAdmission: false,
    nutrition: false,
    parents: false
  });

  const [procedureStarted, setProcedureStarted] = useState(false);

  const sectionsRef = useRef<Record<SectionName, HTMLDivElement | null>>({
    openingConditions: null,
    medications: null,
    roomEquipment: null,
    ventilationEquipment: null,
    intubationEquipment: null,
    apgar: null,
    umbilicalCord: null,
    departmentAdmission: null,
    nutrition: null,
    parents: null
  });

  // No opening field is mandatory. The user may leave the opening form and start
  // the checklist as soon as they submit it (procedureStarted) — even with every
  // field left blank. Sections that depend on a value (e.g. weight-based doses)
  // note the missing value themselves rather than blocking entry up front.
  // On reload, any persisted opening data keeps us past the initial setup.
  const hasOpeningData = useMemo(
    () =>
      !!weightAssessment ||
      gestationalAgeWeeks !== null ||
      !!anamnesis ||
      !!birthReason ||
      !!gender,
    [weightAssessment, gestationalAgeWeeks, anamnesis, birthReason, gender]
  );
  const isInitialSetupComplete = procedureStarted || hasOpeningData;

  const measureSectionHeight = useCallback((name: SectionName) => {
    const section = sectionsRef.current[name];
    if (section) {
      const height = section.scrollHeight;
      section.style.setProperty('--section-height', `${height}px`);
      setTimeout(() => {
        section.style.setProperty('--section-height', '');
      }, 1000);
    }
  }, []);

  const closeOpeningConditionsSection = () => {
    setExpandedSections(prev => ({
      ...prev,
      openingConditions: false
    }));
  };

  const handleOpeningConditionsUpdate = useCallback(() => {
    closeOpeningConditionsSection();
    setProcedureStarted(true);
  }, []);

  const initialPatientInputSubmitted = useCallback(() => {
    closeOpeningConditionsSection();
    setProcedureStarted(true);
  }, []);

  const handleResetAll = useCallback(() => {
    resetContext();
    setProcedureStarted(false);
    setExpandedSections({
      openingConditions: false,
      medications: false,
      roomEquipment: false,
      ventilationEquipment: false,
      intubationEquipment: false,
      apgar: false,
      umbilicalCord: false,
      departmentAdmission: false,
      nutrition: false,
      parents: false
    });
  }, [resetContext]);

  const handleOpenPatientInfo = useCallback(() => {
    setExpandedSections(prev => {
      const willExpand = !prev.openingConditions;
      
      if (willExpand) {
        setTimeout(() => measureSectionHeight('openingConditions'), 0);
      }
      
      return {
        ...prev,
        openingConditions: willExpand
      };
    });
  }, [measureSectionHeight]);

  const toggleSection = useCallback((sectionName: SectionName) => {
    if (!expandedSections[sectionName]) {
      measureSectionHeight(sectionName);
      
      setTimeout(() => {
        const sectionHeader = sectionsRef.current[sectionName]?.previousElementSibling;
        if (sectionHeader) {
          sectionHeader.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 300);
    }
    
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  }, [expandedSections, measureSectionHeight]);

  const sections: Record<SectionName, JSX.Element> = {
    openingConditions: <OpeningConditions onUpdate={handleOpeningConditionsUpdate} />,
    medications: <Medications />,
    roomEquipment: <RoomEquipment />,
    ventilationEquipment: <VentilationEquipment />,
    intubationEquipment: <IntubationEquipment />,
    apgar: <Apgar />,
    umbilicalCord: <UmbilicalCord />,
    departmentAdmission: <DepartmentAdmission />,
    nutrition: <Nutrition />,
    parents: <Parents />
  };

  const getStatusClass = (checked: number, total: number): string => {
    if (checked === 0) return 'status-none';
    if (checked === total) return 'status-complete';
    return 'status-partial';
  };

  const renderApgarBadge = useCallback(() => {
    const parts = [];
    if (apgarStatus.min1 !== null) parts.push(`דקה: ${apgarStatus.min1}`);
    if (apgarStatus.min5 !== null) parts.push(`5 דק': ${apgarStatus.min5}`);
    if (apgarStatus.min10 !== null) parts.push(`10 דק': ${apgarStatus.min10}`);

    if (parts.length === 0) return undefined;

    return (
      <span className="section-status-badge apgar-status">
        {parts.join('\u00A0\u00A0\u00A0\u00A0|\u00A0\u00A0\u00A0\u00A0')}
      </span>
    );
  }, [apgarStatus]);

  const renderSection = useCallback((name: SectionName, icon: any, title: string, index: number, status?: { checked: number; total: number }, customBadge?: JSX.Element) => {
    if (!isInitialSetupComplete && name !== 'openingConditions') return null;

    const backgroundColor = index % 2 === 0 ? '#ffffff' : 'rgb(238, 255, 253)';

    return (
      <div 
        className="transition-all duration-300 section-container" 
        style={{ direction: 'rtl', backgroundColor }}
      >        
        <h4 
          className={`section-header ${expandedSections[name] ? 'expanded' : ''}`} 
          onClick={() => toggleSection(name)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleSection(name);
            }
          }}
        >

          <span className="section-name">
            <FontAwesomeIcon className="section-icon" icon={icon} />
            {title}
            {customBadge || (status && status.checked > 0 && (
              <span className={`section-status-badge ${getStatusClass(status.checked, status.total)}`}>
                {status.checked}/{status.total}
              </span>
            ))}
          </span>
          <span className="toggle-icon" aria-hidden="true">
            {expandedSections[name] ? (
              <ChevronUp size={20} strokeWidth={2.5} />
            ) : (
              <ChevronLeft size={20} strokeWidth={2.5} />
            )}
          </span>
        </h4>
        <div 
          ref={el => sectionsRef.current[name] = el}
          className={`collapsible-section ${
            expandedSections[name] ? 'expanded' : 'collapsed'
          }`}
        >
          <div className="section-content">
            {sections[name]}
          </div>
        </div>
      </div>
    );
  }, [expandedSections, isInitialSetupComplete, sections, toggleSection, getStatusClass, apgarStatus]);

  return (
    <ChecklistsConfigProvider hospital={hospital}>
      <div className="new-preemie-app container main-content">
        <div className="group-container">
          <AppsHeader
            title="קבלת פג ליחידה"
            onReset={handleResetAll}
          />

        {!isInitialSetupComplete ? (
          <div className="opening-conditions-initial-setup-container">
            <OpeningConditions onUpdate={initialPatientInputSubmitted} />
          </div>
        ) : (
          <>
            <div className="patient-info-box" onClick={handleOpenPatientInfo}>
              <b>שבוע הריון:</b> {gestationalAgeWeeks ? `${gestationalAgeWeeks}+${gestationalAgeDays ?? 0}` : 'לא הוזן'},&nbsp;
              <b>הערכת משקל:</b> {weightAssessment ? `${weightAssessment} גרם` : 'לא הוזן'}
            </div>
            <div className="sections-container">
            <div className="sections-list-container">
              {renderSection('openingConditions', faFileLines, 'נתוני פתיחה', 0)}
              {renderSection('medications', faPills, 'תרופות', 1, medicationsStatus)}
              {renderSection('roomEquipment', faBed, 'ציוד - חדר', 2, roomEquipmentStatus)}
              {renderSection('ventilationEquipment', faLungs, 'ציוד - הנשמה', 3, ventilationEquipmentStatus)}
              {renderSection('intubationEquipment', faRepeat, 'ציוד - אינטובציה', 4, intubationEquipmentStatus)}
              {renderSection('apgar', faClock, 'שעון אפגר', 5, undefined, renderApgarBadge())}
              {renderSection('umbilicalCord', faRepeat, 'חיתוך מושהה של חבל הטבור', 6)}
              {renderSection('departmentAdmission', faHospital, 'קבלה למחלקה', 7, departmentAdmissionStatus)}
              {renderSection('nutrition', faUtensils, 'הזנה', 8, nutritionStatus)}
              {renderSection('parents', faPersonBreastfeeding, 'עדכון הורים', 9, parentsStatus)}
            </div>

            {/* PDF Export Button */}
            <NewPreemiePDFExportButton />
          </div>
          </>
        )}
      </div>
    </div>
    </ChecklistsConfigProvider>
  );
};

interface NewPreemieProps {
  hospital?: string;
}

const NewPreemie: React.FC<NewPreemieProps> = ({ hospital }) => {
  return (
    <NewPreemieProvider>
      <NewPreemieContent hospital={hospital} />
    </NewPreemieProvider>
  );
};

export default NewPreemie;
